import { SummaryRow } from '../../../Summary/SummaryRow';
import { Q } from '../../../../store/student/types';
import { IOtherNationality, IRisk } from '../../../../types';
import { boolToYesNo } from '../../../../utilities/formatting';
import { AppliedEUSSStatus, EUSSStatus } from 'api-paths-cas';
import { appliedForEUSSValue } from '../../../pages/personal-info/qPage2/utils';
import { SummaryList } from '../../../Summary/SummaryList';
import { SummaryListItem } from '../../../Summary/SummaryListItem';

interface Props {
  showRisks: boolean;
  nationality?: string;
  countryOfBirth?: string;
  gender?: string;
  appliedForEUSS?: string;
  grantedEUSSStatus?: EUSSStatus;
  applyingFromHomeCountry?: string;
  countryOfVisaApplication?: string;
  livedInUKLast12Months?: boolean;
  otherCountryReason?: string;
  hasOtherNationality: string;
  otherNationalities?: Array<IOtherNationality>;
  dob?: string;
  riskList?: IRisk['personalInfoRisk']['birthAndNationality'];
}

function NationalityAndSex({
  showRisks,
  nationality,
  countryOfBirth,
  gender,
  appliedForEUSS,
  grantedEUSSStatus,
  applyingFromHomeCountry,
  countryOfVisaApplication,
  livedInUKLast12Months,
  otherCountryReason,
  hasOtherNationality,
  otherNationalities,
  dob,
  riskList = [],
}: Props) {
  return (
    <>
      {nationality && (
        <SummaryRow
          key="nationality"
          label="Nationality"
          value={nationality}
          isRisk={showRisks && riskList.includes(Q.NATIONALITY)}
        />
      )}
      {countryOfBirth && (
        <SummaryRow
          key="countryOfBirth"
          label="Country of Birth"
          value={countryOfBirth}
          isRisk={showRisks && riskList.includes(Q.BIRTH_COUNTRY)}
        />
      )}
      {gender && (
        <SummaryRow key="gender" label="Gender" value={gender} isRisk={showRisks && riskList.includes(Q.GENDER)} />
      )}
      {dob && (
        <SummaryRow
          key="dob"
          label="Date of Birth"
          value={dob}
          isDate={true}
          isRisk={showRisks && riskList.includes(Q.DOB)}
        />
      )}
      {appliedForEUSS && (
        <SummaryRow
          key="appliedForEUSS"
          label="EU Settlement Scheme (EUSS)"
          fullLabel="Have you been granted settled or pre-settled status in the UK under the EU Settlement Scheme (EUSS)?"
          value={appliedForEUSSValue(appliedForEUSS as AppliedEUSSStatus)}
          isRisk={showRisks && riskList.includes(Q.APPLIED_FOR_EUSS)}
        />
      )}
      {grantedEUSSStatus && (
        <SummaryRow
          key="grantedEUSSStatus"
          label="EUSS status granted"
          fullLabel="Which status have you been granted?"
          value={grantedEUSSStatus}
        />
      )}
      {applyingFromHomeCountry && (
        <SummaryRow
          key="applyingFromHomeCountry"
          label="Applying From Home Country"
          fullLabel="Will you be applying for your student visa in the same country as your declared nationality?"
          value={applyingFromHomeCountry}
          isRisk={showRisks && riskList.includes(Q.IS_COUNTRY_OF_NATIONALITY)}
        />
      )}
      {countryOfVisaApplication && (
        <SummaryRow
          key="countryOfVisaApplication"
          label="Applying For Visa In Country"
          fullLabel="In which country will you make your visa application?"
          value={countryOfVisaApplication}
        />
      )}
      {countryOfVisaApplication === 'United Kingdom' && (
        <SummaryRow
          key="livedInUK"
          label="UK resident for 12 months"
          fullLabel="Have you lived in the UK for at least 12 months (on the date you will make your visa application)?"
          value={boolToYesNo(livedInUKLast12Months)}
        />
      )}
      {otherCountryReason && (
        <SummaryRow
          key="otherCountryReason"
          label="Other Country Reason"
          fullLabel="Please tell us why you are applying from a country other than the country of your nationality?"
          value={otherCountryReason}
        />
      )}
      {hasOtherNationality && (
        <SummaryRow
          key="hasOtherNationality"
          label="Has Other Nationalities"
          fullLabel="Do you currently hold, or have you ever held, any other nationality or citizenship?"
          value={hasOtherNationality}
          isRisk={showRisks && riskList.includes(Q.HAS_OTHER_NATIONALITY)}
        />
      )}
      {hasOtherNationality && otherNationalities !== undefined && (
        <SummaryRow
          className={'summary'}
          value={otherNationalities?.length}
          label={`How many`}
          fullLabel={'How many nationalities?'}
        />
      )}
      {hasOtherNationality === 'Yes' && otherNationalities !== undefined && (
        <>
          <SummaryList title="Alternative or Previous Nationalities" tooltip="Other Nationality & Is Current">
            {otherNationalities.map((nationality, index) => {
              return (
                <SummaryListItem
                  value={nationality.nationality}
                  subvalue={`${nationality.isCurrent ? 'Current' : 'Former'} nationality`}
                  key={`other-nationality-${index}`}
                />
              );
            })}
          </SummaryList>
        </>
      )}
    </>
  );
}

export default NationalityAndSex;
